import React, { createContext, useContext } from "react";
import { Link } from "react-router-dom";

import { ButtonLink } from "~/shared";
import { icons } from "~/ui";
import { tw } from "~/utils";

interface VisitCardContextType {
  isSelected?: boolean;
}

const VisitCardContext = createContext<VisitCardContextType | null>(null);

const useVisitCardContext = () => {
  const context = useContext(VisitCardContext);
  if (!context) {
    throw new Error("VisitCard components must be used within VisitCard");
  }
  return context;
};

type VisitCardProps = {
  isSelected?: boolean;
  to: string;
  children: React.ReactNode;
  className?: string;
};

const VisitCardRoot = ({
  isSelected = false,
  to,
  children,
  className,
}: VisitCardProps) => {
  return (
    <VisitCardContext.Provider value={{ isSelected }}>
      <Link
        to={to}
        className={tw(
          "hover:bg-violet-25 border-blueGrey focus-visible:ring-violet group flex cursor-pointer items-center justify-between gap-4 rounded-md border bg-white p-4 outline-none transition-all duration-200 hover:border-violet-300 focus-visible:ring-2 focus-visible:ring-offset-2 md:max-w-full",
          isSelected && "border-violet bg-violet-50",
          className,
        )}
      >
        {children}
      </Link>
    </VisitCardContext.Provider>
  );
};

const VisitCardIcon = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-lightLavender/50 flex size-14 shrink-0 items-center justify-center rounded-full">
      {children}
    </div>
  );
};

const VisitCardTitle = ({ children }: { children: React.ReactNode }) => {
  return (
    <h2 className="text-indigo font-sans font-medium md:text-lg">{children}</h2>
  );
};

const VisitCardDescription = ({ children }: { children: React.ReactNode }) => {
  return <p className="text-xs text-gray-700 md:text-base">{children}</p>;
};

const VisitCardArrow = ({ className }: { className?: string }) => {
  const { isSelected } = useVisitCardContext();

  return (
    <div className={tw("flex justify-end", className)}>
      <div
        className={tw(
          "border-indigo flex size-8 items-center justify-center rounded-full border bg-white text-gray-400 transition-colors group-hover:border-violet-300",
          isSelected && "border-violet bg-violet text-white",
        )}
      >
        <icons.outline.ArrowRightIcon className="text-indigo size-4 stroke-2 group-hover:text-violet-300" />
      </div>
    </div>
  );
};

const VisitCardLink = ({
  children,
  className,
  to,
}: {
  children: React.ReactNode;
  to: string;
  className?: string;
}) => {
  return (
    <ButtonLink className={className} to={to}>
      {children}
    </ButtonLink>
  );
};

export const VisitCard = {
  Root: VisitCardRoot,
  Icon: VisitCardIcon,
  Title: VisitCardTitle,
  Description: VisitCardDescription,
  Arrow: VisitCardArrow,
  Link: VisitCardLink,
};
